import { getAccessSession } from '../../features/auth/sessionAccess';
import { getBranchPreviewChatSession } from './branchPreviewSession';
import { viewAsHeaders } from './viewAsSession';

const AUTH_FETCH_LATENCY_PREFIX = '[auth-fetch-latency]';

const nowMs = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

const elapsedMs = (startedAt) => Math.round((nowMs() - startedAt) * 10) / 10;

const logAuthFetchLatency = (event, details = {}) => {
  console.log(`${AUTH_FETCH_LATENCY_PREFIX} ${event}`, {
    at: new Date().toISOString(),
    ...details,
  });
};

export const authFetch = async (url, options = {}) => {
  // Capture before awaiting authentication so an in-flight request keeps its
  // originating chat context if the user switches conversations.
  // Branch preview reads and Actions on the previewed branch name the chat whose working branch it is.
  const previewChat = /\/api\/ontology-(views|actions)/.test(String(url)) ? getBranchPreviewChatSession() : null;
  const viewAs = viewAsHeaders(url);
  const startedAt = nowMs();
  const sessionStartedAt = nowMs();
  const session = await getAccessSession();
  const sessionMs = elapsedMs(sessionStartedAt);

  if (!session) {
    logAuthFetchLatency('session.error', {
      url,
      sessionMs,
      totalMs: elapsedMs(startedAt),
      reason: 'missing-session',
    });
    throw new Error('No session found');
  }

  if (!session.access_token) {
    logAuthFetchLatency('session.error', {
      url,
      sessionMs,
      totalMs: elapsedMs(startedAt),
      reason: 'missing-access-token',
    });
    throw new Error('No access token available');
  }

  // Trusted hosts can bind a confirmed write to the identity that reviewed it.
  const { expectedUserId, ...requestOptions } = options;
  if (expectedUserId && String(session.user?.id || '') !== String(expectedUserId)) {
    throw Object.assign(new Error('Your sign-in changed. Review the action again.'), { status: 409, code: 'unavailable' });
  }

  const headers = {
    ...options.headers,
    ...(previewChat ? { 'X-LineSide-Chat-Session': previewChat } : {}),
    ...viewAs,
    Authorization: `Bearer ${session.access_token}`,
  };

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const authOptions = { ...requestOptions, headers };

  logAuthFetchLatency('request.start', {
    url,
    method: authOptions.method || 'GET',
    sessionMs,
  });

  const fetchStartedAt = nowMs();
  let response;
  try {
    response = await fetch(url, authOptions);
  } catch (error) {
    logAuthFetchLatency('request.error', {
      url,
      method: authOptions.method || 'GET',
      sessionMs,
      fetchMs: elapsedMs(fetchStartedAt),
      totalMs: elapsedMs(startedAt),
      message: error?.message || String(error),
    });
    throw error;
  }

  logAuthFetchLatency('response.headers', {
    url,
    method: authOptions.method || 'GET',
    status: response.status,
    ok: response.ok,
    sessionMs,
    fetchMs: elapsedMs(fetchStartedAt),
    totalMs: elapsedMs(startedAt),
    contentLength: response.headers.get('content-length'),
    contentType: response.headers.get('content-type'),
  });

  return response;
};
